import { ApiService } from 'src/app/services/api.service'
import { ErrorResponse } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { BaseError, Http401Error, Http403Error, HttpError } from './errors'

/**
 * @param redirectOn403 - When true, redirects to /access-denied on 403 errors instead of showing
 *   a toast. Use this for top-level page loads where there's no active page to toast on (e.g.
 *   account list). Default: false (toast + stay on current page).
 */
export function handleArpioError(
  navigatedUrl: string,
  apiService: ApiService,
  uiService: UiService,
  { redirectOn403 = false }: { redirectOn403?: boolean } = {}
) {
  return (error: BaseError) => {
    if (error instanceof Http401Error && apiService) {
      let errorBody = error.body as ErrorResponse
      let authenticateUrl = apiService.buildUrl([errorBody.authenticateUrl])
      apiService.startAuthFlow(authenticateUrl, navigatedUrl).subscribe(
        (redirectUrl: string) => {
          // have to use window.location.href here because router.navigate() can't
          // transition outside of the Angular application.
          window.location.href = redirectUrl
        },
        (error: BaseError) => uiService.announceApiError(error)
      )
    } else if (error instanceof Http403Error) {
      if (redirectOn403) {
        // Top-level load with no active page — redirect so the user has a path forward.
        // Use replace() so the 403-triggering page isn't in history — prevents a
        // back-button loop (access-denied → back → 403 page → redirect → access-denied).
        window.location.replace('/access-denied')
      } else {
        // ARP-6867: Show a toast error instead of redirecting to /access-denied.
        // A 403 is a permission issue, not an auth issue — keep the user on their current page.
        uiService.announceApiError(error)
      }
    } else {
      // Use announceApiError for better error handling and user-friendly messages
      uiService.announceApiError(error)
    }
  }
}

export function getErrorMessage(error: BaseError): string {
  if (error instanceof HttpError) {
    return (error.body as ErrorResponse).message
  } else {
    console.error(error)
    return error.message
  }
}
